import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { FrontendAdminLogs, FrontendUserInfo } from "node-library/dist/portal";
import {
	CommonSwitch,
	CustomRadioGroup,
	CustomSearchBar,
	UserInfoTextInput,
} from "../../../common-components/inputs";
import { Card, Divider, Pagination } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";

import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	FrontendProfileTab,
	Severity,
	SystemRole,
} from "node-library/dist/constants";
import {
	GreenButton,
	OutlinedButton,
	TextButton,
} from "../../../common-components/button";

import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";

import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";
import OrderHistoryPageSkeleton from "../viewOrders/skeletonUI/orderHistoryPageSkeleton";
import LogsPageContent from "./logsPageContent";

interface Props {}

export default function LogsPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [loading, setLoading] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	const [logs, setLogs] = useState<Array<FrontendAdminLogs>>([]);
	const [totalPageCount, setTotalPageCount] = useState<number>(0);

	const [currentPagination, setCurrentPagination] = useState<number>(1);

	async function handleSearch() {
		const trimmedText = searchText.trim();
		if (trimmedText.length < 5) {
			openSnackbar(
				translation("ShortSearchTextError"),
				Severity.ERROR,
				translation("ShortSearchTextErrorDescription"),
				<RedErrorIcon />
			);
			return;
		}

		try {
			setLoading(true);
			const response = await sendPOSTRequest("/logs/search-logs", {
				searchText: trimmedText,
				pageNumber: currentPagination,

				// enableNotificationEmail: enableNotificationEmail,
			});
			if (response?.data?.logs) {
				const pageCount: number = response?.data?.totalPages;
				setLogs(response?.data?.logs);
				setTotalPageCount(pageCount);

				openSnackbar(
					translation("LogsLoaded"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />
				);
			}
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		const trimmedText = searchText.trim();
		if (trimmedText.length >= 5) {
			handleSearch();
		}
	}, [currentPagination]);

	const currentComponent = (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "20px",
				minHeight: "90vh",
				marginTop: "60px",
			}}
		>
			<div
				style={{
					flex: "1",
					display: "flex",
					flexDirection: "column",
					gap: "20px",
				}}
			>
				<div className="view-orders-page-header">
					<div className="typography-heading-01">{translation("Logs")}</div>
					<CustomSearchBar
						id="view-order-page-searchbar"
						searchText={searchText}
						setSearchText={setSearchText}
						placeholder={translation("Search")}
						searchButtonText={translation("Search")}
						onSearch={() => {
							if (currentPagination !== 1) {
								setCurrentPagination(1);
							} else {
								handleSearch();
							}
						}}
					></CustomSearchBar>
				</div>

				{loading ? (
					<OrderHistoryPageSkeleton />
				) : (
					<LogsPageContent logs={logs} />
				)}
			</div>
			{!loading && logs.length > 0 && (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Pagination
						count={totalPageCount}
						page={currentPagination}
						onChange={(e, value) => {
							setCurrentPagination(value);
						}}
						sx={{
							backgroundColor: "white",
							borderRadius: "30px",
							padding: "6px 16px",

							"& .MuiPaginationItem-root": {
								backgroundColor: "white",
								color: "black",
								border: "1px solid transparent", // Hide outline when not selected
								"&:hover": {
									backgroundColor: "#f5f5f5", // Change background on hover
								},
								borderRadius: "50%",
							},
							"& .Mui-selected": {
								backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
								border: `1px solid ${NeutralColor.NEUTRAL_GREY_950}`, // Change text color for selected item
							},
						}}
						variant="outlined"
						shape="rounded"
					/>
				</div>
			)}
		</div>
	);
	return <div style={{ margin: "0 auto" }}>{currentComponent}</div>;
}
